import { addDataToMap } from 'kepler.gl/actions'
import { processRowObject } from 'kepler.gl/processors'
import { fetchCubeData } from '../services/cubeQuery'
import config from './config.json'

export const fetchHarrisCounty =
  ({ variable, token, phase }) =>
  async (dispatch) => {
    try {
      // Add phase to common filters
      const commonFilters = [
        {
          dimension: 'IncidentDamageAssessment.incidentId',
          operator: 'equals',
          values: [variable],
        },
        {
          dimension: 'IncidentDamageAssessment.incidentPhaseCode',
          operator: 'equals',
          values: [phase],
        },
      ]
      // Add phase to structure filters
      const structuresFilter = [
        {
          dimension: 'StructureDamage.incidentId',
          operator: 'equals',
          values: [variable],
        },
        {
          dimension: 'StructureDamage.incidentPhaseCode',
          operator: 'equals',
          values: [phase],
        },
      ]

      const [countyData, destroyed, major, minor, moderate, structure] =
        await Promise.all([
          fetchCubeData('HARRIS_COUNTY', [], token),
          fetchCubeData('DESTROYED_DATA', commonFilters, token),
          fetchCubeData('MAJOR_DATA', commonFilters, token),
          fetchCubeData('MINOR_DATA', commonFilters, token),
          fetchCubeData('MODERATE_DATA', commonFilters, token),
          fetchCubeData('STRUCTURE_DATA', structuresFilter, token),
        ])

      const processedCountyData = processRowObject(countyData.rawData())

      const processedStructureData = processRowObject(structure.rawData())

      // Process damage data using damageCategoryDescription
      const processDataWithDamageLevel = (data, damageLevel) => {
        const rawData = data.rawData()
        const processed = processRowObject(rawData)
        return processed.rows.map((row) => ({
          ...row,
          'IncidentDamageAssessment.damageCategoryDescription': damageLevel,
        }))
      }

      const damageData = {
        fields: [
          ...processRowObject(destroyed.rawData()).fields,
          {
            name: 'IncidentDamageAssessment.damageCategoryDescription',
            type: 'string',
          },
        ],
        rows: [
          ...processDataWithDamageLevel(destroyed, 'DESTROYED'),
          ...processDataWithDamageLevel(major, 'MAJOR'),
          ...processDataWithDamageLevel(minor, 'MINOR'),
          ...processDataWithDamageLevel(moderate, 'MODERATE'),
        ],
      }

      dispatch(
        addDataToMap({
          datasets: [
            {
              info: {
                id: 'county_data',
                label: 'Harris County',
              },
              data: processedCountyData,
            },
            {
              info: {
                id: 'damage_assessment_data',
                label: 'Damage Assessment',
              },
              data: damageData,
            },
            {
              info: {
                id: 'structure_data',
                label: 'Structures',
              },
              data: processedStructureData,
            },
          ],
          options: {
            centerMap: true,
            readOnly: false,
          },
          config: {
            version: 'v1',
            config: {
              mapStyle: {
                styleType: 'light',
              },
              visState: {
                layers: [
                  {
                    id: 'county-layer',
                    type: 'geojson',
                    config: {
                      dataId: 'county_data',
                      label: 'Harris County',
                      columns: {
                        geojson: 'AssessmentArea.assessmentAreaGeometry',
                      },
                      isVisible: true,
                      visConfig: {
                        opacity: 0.5,
                        strokeColor: [151, 14, 45],
                        thickness: 1,
                        filled: false,
                      },
                    },
                  },
                  {
                    id: 'damage-assessment-layer',
                    type: 'geojson',
                    config: {
                      dataId: 'damage_assessment_data',
                      label: 'Damage Assessment',
                      columns: {
                        geojson:
                          'IncidentDamageAssessment.damageAssessmentGeometry',
                      },
                      isVisible: true,
                      filled: true,
                      colorField: {
                        name: 'IncidentDamageAssessment.damageCategoryDescription',
                        type: 'string',
                      },
                      colorScale: 'ordinal',
                      visConfig: {
                        filled: true,
                        fillColor: [255, 0, 0],
                        stroked: true,
                        opacity: 0.2,
                        strokeOpacity: 0.2,
                        thickness: 0.2,
                        strokeColorField: {
                          name: 'IncidentDamageAssessment.damageCategoryDescription',
                          type: 'string',
                        },
                        strokeColorScale: 'ordinal',
                        strokeColorRange: {
                          name: 'Custom',
                          type: 'custom',
                          category: 'Custom',
                          colors: ['#FF0000', '#FFA500', '#FFFF00', '#32CD32'],
                          steps: 4,
                        },
                        colorRange: {
                          name: 'Custom',
                          type: 'custom',
                          category: 'Custom',
                          colors: ['#FF0000', '#FFA500', '#FFFF00', '#32CD32'],
                          steps: 4,
                        },
                        fixedRadius: true,
                        radiusRange: [0, 50],
                        enable3d: false,
                      },
                    },
                    visualChannels: {
                      colorField: {
                        name: 'IncidentDamageAssessment.damageCategoryDescription',
                        type: 'string',
                        domain: ['DESTROYED', 'MAJOR', 'MINOR', 'MODERATE'],
                      },
                      colorScale: 'ordinal',
                      strokeColorField: {
                        name: 'IncidentDamageAssessment.damageCategoryDescription',
                        type: 'string',
                        domain: ['DESTROYED', 'MAJOR', 'MINOR', 'MODERATE'],
                      },
                      strokeColorScale: 'ordinal',
                    },
                  },
                  {
                    id: 'structure-layer',
                    type: 'point',
                    config: {
                      dataId: 'structure_data',
                      label: 'Structures',
                      columns: {
                        lat: 'StructureDamage.latitude',
                        lng: 'StructureDamage.longitude',
                      },
                      isVisible: true,
                      visConfig: {
                        radius: 7,
                        fixedRadius: false,
                        opacity: 5,
                        outline: true,
                        thickness: 5,
                        stroked: true,
                        filled: true,
                        colorField: {
                          name: 'StructureDamage.damageCategoryDescription',
                          type: 'string',
                        },
                        colorScale: 'ordinal',
                        colorRange: {
                          name: 'Custom',
                          type: 'custom',
                          category: 'Custom',
                          colors: ['#FF0000', '#FFA500', '#FFFF00', '#32CD32'],
                          steps: 4,
                        },
                        strokeColorField: {
                          name: 'StructureDamage.damageCategoryDescription',
                          type: 'string',
                        },
                        strokeColorScale: 'ordinal',
                        strokeColorRange: {
                          name: 'Custom',
                          type: 'custom',
                          category: 'Custom',
                          colors: ['#FF0000', '#FFA500', '#FFFF00', '#32CD32'],
                          steps: 4,
                        },
                      },
                    },
                    visualChannels: {
                      colorField: {
                        name: 'StructureDamage.damageCategoryDescription',
                        type: 'string',
                        domain: ['DESTROYED', 'MAJOR', 'MINOR', 'MODERATE'],
                      },
                      colorScale: 'ordinal',
                      strokeColorField: {
                        name: 'StructureDamage.damageCategoryDescription',
                        type: 'string',
                        domain: ['DESTROYED', 'MAJOR', 'MINOR', 'MODERATE'],
                      },
                      strokeColorScale: 'ordinal',
                    },
                  },
                ],
                interactionConfig: {
                  tooltip: {
                    enabled: true,
                    fieldsToShow: {
                      damage_assessment_data: [
                        'IncidentDamageAssessment.damageCategoryDescription',
                      ],
                    },
                  },
                },
              },
            },
          },
        })
      )
    } catch (error) {
      console.error('Error fetching data from Cube.js:', error)
      throw error
    }
  }

export const fetchSubtitles = (token) => async (dispatch) => {
  // Add token parameter here
  try {
    // Fetch data using fetchCubeData
    const incidentData = await fetchCubeData('INCIDENT', [], token)
    const rawData = processRowObject(incidentData.rawData())

    // Ensure rawData contains fields and rows
    if (!rawData || !rawData.fields || !rawData.rows) {
      throw new Error('Invalid data structure returned from rawData()')
    }

    const { rows, fields } = rawData

    // Map fields to their indices for dynamic processing
    const fieldIndexMap = fields.reduce((acc, field, idx) => {
      acc[field.name] = idx
      return acc
    }, {})

    // Process the rows into the desired format
    const processedSubtitles = rows.map((row) => ({
      name: row[fieldIndexMap['Incident.incidentName']] || 'Unknown Name', // Access incidentName using index
      description:
        row[fieldIndexMap['Incident.incidentDescription']] || 'No Description', // Access incidentDescription using index
      id: row[fieldIndexMap['Incident.incidentId']] || 'No Id',
      nowCast: row[fieldIndexMap['Incident.nowcastAvailable']] || 'false',
      postCast: row[fieldIndexMap['Incident.postcastAvailable']] || 'false',
      foreCast: row[fieldIndexMap['Incident.forecastAvailable']] || 'false',
    }))
    console.log(
      'Processed processedSubtitles:',
      JSON.stringify(processedSubtitles)
    )
    // Dispatch the processed data to Redux store
    dispatch({
      type: 'FETCH_SUBTITLES_SUCCESS',
      payload: processedSubtitles,
    })
  } catch (error) {
    console.error('Error fetching subtitles:', error.message)
    dispatch({
      type: 'FETCH_SUBTITLES_ERROR',
      error: error.message,
    })
  }
}

export const fetchStructureCount =
  (variable, token, drawnPolygon, phase) => async (dispatch) => {
    console.log('drawnPolygon: ' + drawnPolygon)
    try {
      // Always provide a polygon value. If drawnPolygon is empty or null, default to "null"
      const polygonValue =
        drawnPolygon && drawnPolygon.trim() !== '' ? drawnPolygon : 'null'

      // Build filters using member-based keys
      const structureCountFilter = [
        {
          member: 'StructuresInPolygon.polygonWKT',
          operator: 'equals',
          values: [polygonValue],
        },
        {
          member: 'StructuresInPolygon.incidentId',
          operator: 'equals',
          values: [variable.toString()],
        },
        {
          member: 'StructuresInPolygon.incidentPhaseCode',
          operator: 'equals',
          values: [phase],
        },
      ]

      // Since our cube always expects a polygon parameter,
      // use the cube that is configured to handle it.
      const cubeDataType = 'STRUCTURE_COUNT_DATA'
      const responseKey = 'StructuresInPolygon.structureCount'

      // Fetch data from Cube.js
      const response = await fetchCubeData(
        cubeDataType,
        structureCountFilter,
        token
      )
      const rawData = response.rawData()
      const structureCount = rawData?.[0]?.[responseKey] ?? 0

      dispatch({
        type: 'FETCH_STRUCTURE_COUNT_SUCCESS',
        payload: structureCount,
      })
      return structureCount
    } catch (error) {
      console.error('Error fetching structure count:', error)
      dispatch({
        type: 'FETCH_STRUCTURE_COUNT_ERROR',
        error: error.message,
      })
      throw error
    }
  }

export const fetchOccupancyData =
  (variable, token, drawnPolygon, phase) => async (dispatch) => {
    try {
      const occupancyTypeFilter = [
        {
          member: 'StructureDamagePolygon.incidentId',
          operator: 'equals',
          values: [variable],
        },
        {
          member: 'StructureDamagePolygon.polygonWKT',
          operator: 'equals',
          values: [drawnPolygon || 'null'],
        },
        {
          member: 'StructureDamagePolygon.incidentPhaseCode',
          operator: 'equals',
          values: [phase],
        },
      ]

      const response = await fetchCubeData(
        'OCCUPANCY_TYPE_DATA',
        occupancyTypeFilter,
        token
      )

      const rawData = response.rawData()

      dispatch({
        type: 'FETCH_OCCUPANCY_TYPE_SUCCESS',
        payload: rawData,
      })

      return rawData
    } catch (error) {
      console.error('Error fetching occupancy data:', error)
      dispatch({
        type: 'FETCH_OCCUPANCY_TYPE_ERROR',
        error: error.message,
      })
      throw error
    }
  }

export const fetchSeverityData =
  (variable, token, drawnPolygon, phase) => async (dispatch) => {
    try {
      // Always provide a polygon parameter. If drawnPolygon is empty (or whitespace), default to "null"
      const polygonValue =
        drawnPolygon && drawnPolygon.trim() !== '' ? drawnPolygon : 'null'

      // The incidentId filter is first so that it binds to $1, and the polygon filter is second for $2.
      const damageSeverityFilter = [
        {
          member: 'IncidentDamageCategorySummary.incidentId',
          operator: 'equals',
          values: [variable.toString()],
        },
        {
          member: 'IncidentDamageCategorySummary.polygonWKT',
          operator: 'equals',
          values: [polygonValue],
        },
        {
          member: 'IncidentDamageCategorySummary.incidentPhaseCode',
          operator: 'equals',
          values: [phase],
        },
        // If additional filters (such as a damage category rank) are required, you can add them here.
      ]

      const response = await fetchCubeData(
        'DAMAGE_SEVERITY_DATA',
        damageSeverityFilter,
        token
      )

      const rawData = response.rawData()

      dispatch({
        type: 'FETCH_DAMAGE_SEVERITY_SUCCESS',
        payload: rawData,
      })

      return rawData
    } catch (error) {
      console.error('Error fetching severity data:', error)
      dispatch({
        type: 'FETCH_DAMAGE_SEVERITY_ERROR',
        error: error.message,
      })
      throw error
    }
  }

export const fetchDamageEstimateData =
  (variable, token, drawnPolygon, phase) => async (dispatch) => {
    try {
      const damageEstimateDataFilter = [
        {
          dimension: 'StructureDamageEstimate.incidentId',
          operator: 'equals',
          values: [variable],
        },
        {
          dimension: 'StructureDamageEstimate.polygonWKT',
          operator: 'equals',
          values: [drawnPolygon ? drawnPolygon : 'null'],
        },
        {
          member: 'StructureDamageEstimate.incidentPhaseCode',
          operator: 'equals',
          values: [phase],
        },
      ]

      const response = await fetchCubeData(
        'DAMAGE_ESTIMATE_DATA',
        damageEstimateDataFilter,
        token
      )

      const rawData = response.rawData()
      const estimateData =
        rawData?.[0]?.['StructureDamageEstimate.totalDamageEstimate'] ?? 0

      dispatch({
        type: 'FETCH_DAMAGE_ESTIMATE_DATA_SUCCESS',
        payload: estimateData,
      })

      return estimateData
    } catch (error) {
      console.error('Error fetching occupancy data:', error)
      dispatch({
        type: 'FETCH_DAMAGE_ESTIMATE_DATA_ERROR',
        error: error.message,
      })
      throw error
    }
  }
